import { Provenance } from '@medplum/fhirtypes';
import { Box, Group, Text } from '@mantine/core';
import { formatHumanName } from '@medplum/core';
import dayjs from 'dayjs';
import { IconLock } from '@tabler/icons-react';
import { TimelineNote } from '@/components/Timeline/types';
import { formatToAdobeSpectrumFormat, getTimestamp } from '@/services/date';
import { OrganizationFragment, ProfileFragment } from '@/services/graphql';

type EncounterHistoryProps = {
  isFinished: boolean;
  provenance?: Provenance;
  notes: TimelineNote[];
};

export function EncounterHistory({ provenance, notes }: EncounterHistoryProps) {
  const provenanceInfo = getProvenanceInformation(provenance);
  const addendumsInfo = getAddendumInformation(notes);
  const historyItems = [provenanceInfo, ...addendumsInfo].filter(Boolean).sort((a, b) => {
    return dayjs(b?.recorded).diff(dayjs(a?.recorded));
  });

  if (historyItems.length === 0) {
    return null;
  }

  return (
    <Box
      mt="sm"
      pl="sm"
      pr="sm"
      pt="2px"
      style={{
        border: '1px solid #e9ecef',
        borderRadius: '12px',
      }}
    >
      {historyItems.map((item, index) => (
        <EncounterHistoryItem key={index} data={item} isLast={index === historyItems.length - 1} />
      ))}
    </Box>
  );
}

function EncounterHistoryItem({ data, isLast }: { data?: HistoryItemInformation; isLast: boolean }) {
  if (!data) {
    return null;
  }
  const { action, recorded, content } = data;
  return (
    <Box pt="sm" pb="sm" style={{ borderBottom: isLast ? 'none' : '1px solid #e9ecef' }}>
      <Group justify="space-between">
        <Group gap={6} wrap="wrap">
          <IconLock size={16} color="#7C838A" />
          <Text size="sm" c="gray.9" style={{ fontWeight: 600 }}>
            {action}
          </Text>
        </Group>

        {recorded && (
          <Text size="sm" c="gray.5">
            {recorded}
          </Text>
        )}
      </Group>
      {content && (
        <Text size="sm" ml="4px" mt="4px">
          {content}
        </Text>
      )}
    </Box>
  );
}

interface HistoryItemInformation {
  action: string;
  recorded: string;
  content?: string;
}

function getProvenanceInformation(provenance?: Provenance): HistoryItemInformation | undefined {
  if (provenance) {
    const recorded = provenance.recorded ? formatToAdobeSpectrumFormat(provenance.recorded) : '';
    const agent = getAgentLabel(provenance);
    const reason = getReasonLabel(provenance);
    return { action: `${reason} by ${agent}`, recorded };
  }

  return undefined;
}

function getAgentLabel(provenance: Provenance): string {
  const who = provenance.agent?.[0]?.who;
  return who?.display ?? who?.reference ?? '';
}

function getReasonLabel(provenance: Provenance): string {
  const REASON_MAP = {
    SIGN: 'Signed and locked',
  };
  const reason = provenance.reason?.[0]?.coding?.[0];
  const code = reason?.code;

  return REASON_MAP[code as keyof typeof REASON_MAP] ?? '';
}

function getSenderDisplayName(sender?: ProfileFragment | OrganizationFragment): string {
  if (!sender) return 'Unknown';

  if (sender.resourceType === 'Organization') {
    // typescript must know this is OrganizationFragment
    return sender.name;
  }

  return formatHumanName(sender.name?.[0]) || 'Unknown';
}

function getAddendumInformation(notes: TimelineNote[]): HistoryItemInformation[] {
  return notes.map((note) => {
    const senderName = getSenderDisplayName(note.sender);
    return {
      action: `Addended by ${senderName}`,
      recorded: getTimestamp(note),
      content: note.content,
    };
  });
}
